import { defineStore } from 'pinia'
import * as GameApi from '@/api/game'

// 添加类型定义
interface VipConfig {
  level: number
  icon: string
  advanceBet: number
  // 彩金奖励
  advanceRewardBound: number
  advanceReward: number
  advanceRecharge: number
  dayReward: number
  // 日彩金奖励
  dayRewardBound: number

  weekReward: number
  // 周彩金奖励
  weekRewardBound: number
  monthReward: number
  // 月彩金奖励
  monthRewardBound: number
  reserveGradeRecharge: number
  reserveGradeBet: number
  // 提现金额
  withdrawAmount: number
  // 提现次数
  withdrawCount: number
}

interface VipInfo {
  level: number
  bet: number
  recharge: number
  currentVip: {
    icon: string
    conditions: Array<{ value: number }>
  }
}

interface VipData {
  configList: VipConfig[]
  drawRewardList: number[]
  openReward: number[]
  level: number
  bet: number
  recharge: number
  reserveGradeSwitch?: boolean
}

export const useVipStore = defineStore('vip', {
  state: () => ({
    vipInfo: {} as VipInfo,
    vipData: {} as VipData,
    loading: false,
    hasLoaded: false
  }),

  getters: {
    // 升级奖励数据
    upgradeRewardData: (state) => {
      const configList = state.vipData.configList || []
      return configList.map(config => ({
        level: config.level,
        icon: config.icon,
        requiredBet: config.advanceBet, // 需要投注
        reward: config.advanceReward, // 奖励
        // 彩金奖励
        rewardBound: config.advanceRewardBound, // 奖励
        requiredRecharge: config.advanceRecharge // 需要充值
      }))
    },

    // 日奖励数据
    dailyRewardData: (state) => {
      const configList = state.vipData.configList || []
      return configList.map(config => ({
        level: config.level,
        icon: config.icon,
        reward: config.dayReward, // 日奖励
        // 日彩金奖励
        rewardBound: config.dayRewardBound, // 日彩金奖励
      }))
    },

    // 周奖励数据
    weeklyRewardData: (state) => {
      const configList = state.vipData.configList || []
      return configList.map(config => ({
        level: config.level,
        icon: config.icon,
        reward: config.weekReward, // 周奖励
        // 周彩金奖励
        rewardBound: config.weekRewardBound, // 周彩金奖励
      }))
    },

    // 月奖励数据
    monthlyRewardData: (state) => {
      const configList = state.vipData.configList || []
      return configList.map(config => ({
        level: config.level,
        icon: config.icon,
        reward: config.monthReward, // 月奖励
        // 月彩金奖励
        rewardBound: config.monthRewardBound, // 月彩金奖励
      }))
    },

    // 保级数据
    maintenanceData: (state) => {
      const configList = state.vipData.configList || []
      return configList.map(config => ({
        level: config.level,
        recharge: config.reserveGradeRecharge, // 保级充值
        bet: config.reserveGradeBet // 保级流水
      }))
    }
  },

  actions: {
    async loadVipData(forceRefresh = false) {
      // 如果正在加载中，等待加载完成
      if (this.loading) {
        // 等待加载完成
        while (this.loading) {
          await new Promise(resolve => setTimeout(resolve, 10))
        }
        return
      }

      // 如果已经加载过且不是强制刷新，直接返回
      if (!forceRefresh && this.hasLoaded) {
        return
      }

      this.loading = true
      try {
        const vipInfoRes = await GameApi.getUserVipInfo()
        const data = vipInfoRes.data

        // 根据当前level找到对应的配置
        const currentVip = data.configList?.find(config => config.level === data.level)
        if (currentVip) {
          // 构建vipInfo，包含currentVip字段供VIP页面使用
          this.vipInfo = {
            level: data.level,
            bet: data.bet,
            recharge: data.recharge,
            currentVip: {
              icon: currentVip.icon, // 等级头像
              conditions: [
                { value: currentVip.advanceRecharge || 0 }, // 充值条件
                { value: currentVip.advanceBet || 0 }       // 投注条件
              ]
            }
          }
        }

        // vipData包含完整的配置数据
        this.vipData = data
        this.hasLoaded = true
      } finally {
        this.loading = false
      }
    }
  }
}) 